import React, {createContext, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import citizenService from "../../../../../../services/web/citizen-service.js";
import initialCitizen from "../resources/initial-citizen.js";
import useFetchData from "../hooks/use-fetch-data.js";
import normalizeCitizenData from "../utils/normalize-citizen-data.js";
import useCitizenAttaches from "../hooks/use-citizen-attaches.js";
import {FaAddressBook, FaCar, FaPaperclip, FaUser, FaUsers} from "react-icons/fa";
import {FaHouse} from "react-icons/fa6";
import useHasPermission from "../../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../../utils/enums/permission-enum.js";
import "../../../css/citizen.css"
import placeService from "../../../../../../services/web/place-service.js";

export const CitizenContext = createContext(undefined);

const citizenTabs = [
    {id: "resume", key: "resume", label: "Resumo"},
    {
        id: "protected",
        key: "",
        label: "Cidadão/Empresa",
        children: [
            {key: "general", label: "Identificação", icon: <FaUser/>},
            {key: "address", label: "Endereços", icon: <FaHouse/>},
            {key: "contacts", label: "Contatos", icon: <FaAddressBook/>},
            {key: "vehicles", label: "Veículos", icon: <FaCar/>},
            {key: "attaches", label: "Anexos", icon: <FaPaperclip/>},
            {key: "role", label: "Agentes que receberão os alertas no APP", icon: <FaUsers className={'shrink-0!'}/>},
        ]
    },
];

const CitizenProvider = ({children}) => {
    const {id} = useParams();

    const hasPermission = useHasPermission();

    const fetchData = useFetchData();

    const [citizen, setCitizen] = useState(initialCitizen);
    const [attaches, setAttaches] = useState([]);
    const [attachesDescription, setAttachesDescription] = useState({})
    const [aggressors, setAggressors] = useState([]);
    const [tabs, setTabs] = useState(citizenTabs);
    const [isLoading, setIsLoading] = useState(false);

    const files = useCitizenAttaches(citizen, attaches, aggressors);
    const citizenStatus = useRef(true);

    const sosView = hasPermission(PermissionEnum.SOS_VIEW);
    const citizenManage = hasPermission(PermissionEnum.SOS_MANAGE_CITIZEN);
    const eventManage = hasPermission(PermissionEnum.SOS_MANAGE_EVENTS);

    const firstRender = useRef(true);
    const [originalSnapshot, setOriginalSnapshot] = useState(() => id ? null : {
        citizen: initialCitizen,
        attaches: [],
        aggressors: [],
        attachesDescription: {}
    });

    const onlyView = useRef(!citizenManage && (sosView || eventManage));

    const { data, isSuccess,refetch, isLoading: isLoadingFetch } = useQuery({
        queryKey: ["citizen", id],
        enabled: !!id,
        queryFn: () => citizenService.get(id)
    });

    const { data: places, isSuccess: placeSuccess } = useQuery({
        queryKey: ["allPlaces", id],
        enabled: !!id,
        queryFn: () => placeService.list(id)
    });

    useEffect(() => {
        if (!isSuccess || !placeSuccess) return;

        const parsed = normalizeCitizenData(data, places.data.data);

        setCitizen(parsed.citizen);
        setAttaches(parsed.attaches);
        setAttachesDescription(parsed.attachesDescription);
        setAggressors(parsed.aggressors);

        citizenStatus.current = parsed.status;
        setOriginalSnapshot({
            citizen: parsed.citizen,
            attaches: parsed.attaches,
            aggressors: parsed.aggressors,
            attachesDescription: parsed.attachesDescription
        });

        if (onlyView.current) {
            setTabs([{id: "resume", key: "resume", label: "Resumo"}])
        }

        firstRender.current = false;
    }, [data, placeSuccess]);

    const attachMeta = (list) => list.map(({ attachId, name, size }) => ({ attachId, name, size }));

    const markSaved = useCallback(() => {
        setOriginalSnapshot({citizen, attaches, aggressors, attachesDescription});
    }, [citizen, attaches, aggressors, attachesDescription]);

    const isDirty = useMemo(() => {
        if (!originalSnapshot) return false;
        const { citizen: oc, attaches: oa, aggressors: og, attachesDescription: oad } = originalSnapshot;
        return (
            JSON.stringify(citizen) !== JSON.stringify(oc) ||
            JSON.stringify(attachMeta(attaches)) !== JSON.stringify(attachMeta(oa)) ||
            JSON.stringify(aggressors) !== JSON.stringify(og) ||
            JSON.stringify(attachesDescription) !== JSON.stringify(oad)
        );
    }, [citizen, attaches, aggressors, attachesDescription, originalSnapshot]);

    const value = useMemo(
        () => ({
            id,
            citizen, setCitizen,
            attaches, setAttaches,
            attachesDescription, setAttachesDescription,
            aggressors, setAggressors,
            fetchData, refetch, citizenStatus,
            isLoadingFetch, files, tabs, setTabs,
            onlyView: onlyView.current,
            sosView, citizenManage,
            isDirty, markSaved,
            isLoading, setIsLoading
        }),
        [id, citizen, attaches, attachesDescription, aggressors, fetchData, refetch, isLoadingFetch, files, tabs, isDirty, isLoading, markSaved]
    );

    return (
        <CitizenContext.Provider value={value}>
            {children}
        </CitizenContext.Provider>
    );
};

export default CitizenProvider;